<template>
  <b-modal
    id="edit-user-modal"
    ref="editUserModal"
    title="Edit Internal User"
    size="lg"
    hide-footer
    no-close-on-backdrop
    @hidden="resetForm"
  >
    <div v-if="isLoadingDetail" class="text-center py-5">
      <b-spinner></b-spinner>
      <p class="mt-2 mb-0">Loading user…</p>
    </div>

    <ValidationObserver v-else v-slot="{ passes }">
      <form @submit.prevent="passes(submitForm)">
        <div class="row">
          <!-- First Name -->
          <div class="col-md-6">
            <ValidationProvider name="First Name" rules="required" v-slot="{ errors }">
              <div class="form-group">
                <label for="edit-firstname">
                  First Name
                  <span class="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="edit-firstname"
                  v-model="formData.firstname"
                  class="form-control"
                  :class="{ 'is-invalid': errors[0] }"
                  placeholder="Enter first name"
                />
                <span class="text-danger error-msg">{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </div>

          <!-- Last Name -->
          <div class="col-md-6">
            <ValidationProvider name="Last Name" rules="required" v-slot="{ errors }">
              <div class="form-group">
                <label for="edit-lastname">
                  Last Name
                  <span class="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="edit-lastname"
                  v-model="formData.lastname"
                  class="form-control"
                  :class="{ 'is-invalid': errors[0] }"
                  placeholder="Enter last name"
                />
                <span class="text-danger error-msg">{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </div>

          <!-- Email (read-only — login identity, immutable here) -->
          <div class="col-md-6">
            <div class="form-group">
              <label for="edit-email">Email</label>
              <input
                type="email"
                id="edit-email"
                :value="formData.email"
                class="form-control"
                readonly
                disabled
              />
              <small class="text-muted">Email is the login identity and cannot be changed here.</small>
            </div>
          </div>

          <!-- Phone Number -->
          <div class="col-md-6">
            <ValidationProvider name="Phone Number" rules="required" v-slot="{ errors }">
              <div class="form-group">
                <label for="edit-phonenumber">
                  Phone Number
                  <span class="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="edit-phonenumber"
                  v-model="formData.phonenumber"
                  class="form-control"
                  :class="{ 'is-invalid': errors[0] }"
                  placeholder="Enter phone number"
                />
                <span class="text-danger error-msg">{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </div>

          <!-- Country -->
          <div class="col-md-6">
            <ValidationProvider name="Country" rules="required" v-slot="{ errors }">
              <div class="form-group">
                <label for="edit-countryid">
                  Country
                  <span class="text-danger">*</span>
                </label>
                <b-form-select
                  id="edit-countryid"
                  v-model="formData.countryid"
                  :options="countryOptions"
                  class="form-control"
                  :class="{ 'is-invalid': errors[0] }"
                >
                  <template #first>
                    <b-form-select-option :value="null" disabled>
                      -- Select Country --
                    </b-form-select-option>
                  </template>
                </b-form-select>
                <span class="text-danger error-msg">{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </div>

          <!-- Role -->
          <div class="col-md-6">
            <ValidationProvider name="Role" rules="required" v-slot="{ errors }">
              <div class="form-group">
                <label for="edit-roleid">
                  Role
                  <span class="text-danger">*</span>
                </label>
                <b-form-select
                  id="edit-roleid"
                  v-model="formData.roleid"
                  :options="roleOptions"
                  class="form-control"
                  :class="{ 'is-invalid': errors[0] }"
                >
                  <template #first>
                    <b-form-select-option :value="null" disabled>
                      -- Select Role --
                    </b-form-select-option>
                  </template>
                </b-form-select>
                <span class="text-danger error-msg">{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </div>

          <!-- Function -->
          <div class="col-md-6">
            <ValidationProvider name="Function" rules="required" v-slot="{ errors }">
              <div class="form-group">
                <label for="edit-functionid">
                  Function
                  <span class="text-danger">*</span>
                </label>
                <b-form-select
                  id="edit-functionid"
                  v-model="formData.functionid"
                  :options="functionOptions"
                  class="form-control"
                  :class="{ 'is-invalid': errors[0] }"
                >
                  <template #first>
                    <b-form-select-option :value="null" disabled>
                      -- Select Function --
                    </b-form-select-option>
                  </template>
                </b-form-select>
                <span class="text-danger error-msg">{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </div>

          <!-- Access Type -->
          <div class="col-md-6">
            <ValidationProvider name="Access Type" rules="required" v-slot="{ errors }">
              <div class="form-group">
                <label for="edit-accesstypeid">
                  Access Type
                  <span class="text-danger">*</span>
                </label>
                <b-form-select
                  id="edit-accesstypeid"
                  v-model="formData.accesstypeid"
                  :options="accessTypeOptions"
                  class="form-control"
                  :class="{ 'is-invalid': errors[0] }"
                >
                  <template #first>
                    <b-form-select-option :value="null" disabled>
                      -- Select Access Type --
                    </b-form-select-option>
                  </template>
                </b-form-select>
                <span class="text-danger error-msg">{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </div>

          <!-- Can Process HQ Transactions (not applicable to SuperAdmin) -->
          <div class="col-md-6" v-if="!isSuperAdminRoleSelected">
            <div class="form-group">
              <div class="custom-control custom-checkbox">
                <input
                  type="checkbox"
                  class="custom-control-input"
                  id="edit-canProcessHqTx"
                  v-model="formData.can_process_hq_tx"
                />
                <label class="custom-control-label" for="edit-canProcessHqTx">
                  Can process HQ transactions only
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- API Error Message -->
        <div v-if="apiError" class="alert alert-danger" role="alert">
          {{ apiError }}
        </div>

        <!-- Action Buttons -->
        <div class="d-flex justify-content-end mt-4">
          <b-button
            variant="outline-secondary"
            size="lg"
            class="mr-3"
            @click="hideModal"
            :disabled="isSubmitting"
          >
            Cancel
          </b-button>
          <b-button type="submit" variant="primary" size="lg" :disabled="isSubmitting">
            <span v-if="isSubmitting">
              <b-spinner small></b-spinner>
              Saving...
            </span>
            <span v-else>Save Changes</span>
          </b-button>
        </div>
      </form>
    </ValidationObserver>
  </b-modal>
</template>

<script>
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { InternalUserService } from "@/services/internalUserService";
import { mapGetters } from "vuex";

const EMPTY_FORM = () => ({
  firstname: "",
  lastname: "",
  email: "",
  phonenumber: "",
  countryid: null,
  roleid: null,
  functionid: null,
  accesstypeid: null,
  can_process_hq_tx: false,
});

export default {
  name: "EditUserModal",
  components: {
    ValidationObserver,
    ValidationProvider,
  },
  data() {
    return {
      userid: null,
      formData: EMPTY_FORM(),
      isSubmitting: false,
      isLoadingDetail: false,
      apiError: null,
      roleOptions: [],
      functionOptions: [],
      accessTypeOptions: [],
      countryOptions: [],
    };
  },
  computed: {
    ...mapGetters(["countryList"]),
    // True when the selected role is SuperAdmin. SuperAdmins are never HQ-restricted,
    // so the "Can process HQ transactions only" option does not apply to them.
    isSuperAdminRoleSelected() {
      const selected = this.roleOptions.find((r) => r.value === this.formData.roleid);
      return !!selected && (selected.text || "").toLowerCase() === "superadmin";
    },
  },
  watch: {
    isSuperAdminRoleSelected(isSuperAdmin) {
      if (isSuperAdmin) {
        this.formData.can_process_hq_tx = false;
      }
    },
    countryList: {
      immediate: true,
      handler(newValue) {
        if (newValue && newValue.length > 0) {
          this.countryOptions = newValue.map((country) => ({
            value: country.countryid,
            text: country.countryname || country.name,
          }));
        }
      },
    },
  },
  mounted() {
    this.loadDropdownOptions();
  },
  methods: {
    async show(userid) {
      this.userid = userid;
      this.apiError = null;
      this.$refs.editUserModal.show();
      await this.fetchUser(userid);
    },

    hideModal() {
      this.$refs.editUserModal.hide();
    },

    resetForm() {
      this.userid = null;
      this.formData = EMPTY_FORM();
      this.apiError = null;
      this.isSubmitting = false;
      this.isLoadingDetail = false;
    },

    async fetchUser(userid) {
      this.isLoadingDetail = true;
      try {
        const response = await InternalUserService.getUser(userid);
        const d = response.data?.details;
        if (d) {
          this.formData = {
            firstname: d.firstname || "",
            lastname: d.lastname || "",
            email: d.email || "",
            phonenumber: d.phone || "",
            countryid: d.countryid || null,
            roleid: d.roleid || null,
            functionid: d.functionid || null,
            accesstypeid: d.accesstypeid || null,
            can_process_hq_tx: !!d.canProcessHqTx,
          };
        } else {
          this.apiError = "Failed to load user details.";
        }
      } catch (error) {
        console.error("Error loading user details:", error);
        this.apiError =
          error.response?.data?.message || "Failed to load user details. Please try again.";
        this.$toast.error(this.apiError, { position: "top-right" });
      } finally {
        this.isLoadingDetail = false;
      }
    },

    async loadDropdownOptions() {
      try {
        const [rolesRes, functionsRes, accessTypesRes] = await Promise.all([
          InternalUserService.getRoles(),
          InternalUserService.getFunctions(),
          InternalUserService.getAccessTypes(),
        ]);

        if (rolesRes.data?.details && Array.isArray(rolesRes.data.details)) {
          this.roleOptions = rolesRes.data.details.map((role) => ({
            value: role.id,
            text: role.name,
          }));
        }

        if (functionsRes.data?.details && Array.isArray(functionsRes.data.details)) {
          this.functionOptions = functionsRes.data.details.map((func) => ({
            value: func.id,
            text: func.name,
          }));
        }

        if (accessTypesRes.data?.details && Array.isArray(accessTypesRes.data.details)) {
          this.accessTypeOptions = accessTypesRes.data.details.map((type) => ({
            value: type.id,
            text: type.name,
          }));
        }
      } catch (error) {
        console.error("Error loading dropdown options:", error);
        this.$toast.error("Failed to load dropdown options. Please refresh the page.", {
          position: "top-right",
        });
      }
    },

    async submitForm() {
      if (!this.userid) return;
      this.isSubmitting = true;
      this.apiError = null;

      try {
        const payload = {
          firstname: this.formData.firstname,
          lastname: this.formData.lastname,
          phonenumber: this.formData.phonenumber,
          countryid: this.formData.countryid,
          roleid: this.formData.roleid,
          functionid: this.formData.functionid,
          accesstypeid: this.formData.accesstypeid,
          can_process_hq_tx: this.formData.can_process_hq_tx,
        };

        const response = await InternalUserService.updateUser(this.userid, payload);

        // Backend success code for InternalUser category (0014) is '0014-000'.
        const code = response.data?.code;
        const updated = code === "0014-000" || !!response.data?.details;

        if (updated) {
          this.$toast.success(response.data?.message || "User updated successfully!", {
            position: "top-right",
          });
          this.$emit("user-updated");
          this.hideModal();
        } else {
          this.apiError = response.data?.message || "Failed to update user. Please try again.";
          this.$toast.error(this.apiError, { position: "top-right" });
        }
      } catch (error) {
        console.error("Error updating user:", error);
        const errorMessage =
          error.response?.data?.message ||
          "An error occurred while updating the user. Please try again.";
        const errorDetail = error.response?.data?.detail;
        this.apiError = errorDetail ? `${errorMessage}: ${errorDetail}` : errorMessage;
        this.$toast.error(this.apiError, { position: "top-right" });
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.error-msg {
  font-size: 0.875rem;
  display: block;
  margin-top: 0.25rem;
}

.form-group label {
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.custom-control-label {
  font-weight: normal;
}
</style>
